/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List } from 'react-virtualized';

import { Box, BoxProps, Typography } from '@mui/material';

import { HomePageFavoriteServiceLoader } from '../../../skeletons/HomePageFavoriteServiceLoader';
import { IUser } from '../../../types/user/IUser';
import { FavoriteService, useFavoriteServices } from '../../../utils/useFavoriteServices';
import { FavoriteServiceCard } from './FavoriteServiceCard';

type FavoriteServicesProps = {
  user: IUser;
  services: FavoriteService[];
  organizationsDisplayed: boolean;
  isLoading: boolean;
} & BoxProps;

const FavoriteServices = memo(({ services, organizationsDisplayed, isLoading, ...rest }: FavoriteServicesProps) => {
  const { t } = useTranslation();
  const listRef = useRef<List | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { favoriteServices, toggleFavorite } = useFavoriteServices();
  const favoriteServiceIds = useMemo(() => favoriteServices.map((fs: { id: string }) => fs.id), [favoriteServices]);
  const [filteredServices, setFilteredServices] = useState(() =>
    services.filter(service => favoriteServiceIds.includes(service.id)),
  );

  useEffect(() => {
    const updatedServices = services.filter(service => favoriteServiceIds.includes(service.id));

    setFilteredServices(prevServices => {
      if (JSON.stringify(prevServices) === JSON.stringify(updatedServices)) {
        return prevServices;
      }
      return updatedServices;
    });

    if (listRef.current) {
      listRef.current.forceUpdateGrid();
    }
  }, [favoriteServiceIds, services]);

  const rowRenderer = useCallback(
    ({ index, key, style }: { index: number; key: string; style: CSSProperties }) => {
      const service = filteredServices[index];

      return (
        <Box key={key} style={style} sx={{ display: 'flex', alignItems: 'stretch' }}>
          <FavoriteServiceCard
            toggleFavorite={toggleFavorite}
            organizationDisplayed={organizationsDisplayed}
            id={service.id}
            project={service.project}
            organization={service.organization}
            name={service.name}
            type={service.type}
          />
        </Box>
      );
    },
    [filteredServices, organizationsDisplayed, toggleFavorite],
  );

  return (
    <>
      {isLoading && <HomePageFavoriteServiceLoader />}
      {!isLoading && (
        <Box
          component="div"
          sx={{
            border: '1px solid #EEEEEE',
            margin: 0,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'hidden',
          }}
          {...rest}
        >
          <Typography mt={3} ml={3} variant="h4" component="h4" gutterBottom>
            {t('common:home.your_favorite_services')}
          </Typography>
          <Box
            ref={containerRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              overflowY: 'auto',
              height: filteredServices.length > 0 ? Math.max(filteredServices.length * 100, 300) : 300,
            }}
          >
            {!isLoading && filteredServices.length === 0 && (
              <Typography ml={3} fontSize={'13px'}>
                {t('common:home.no_favorite_services')}
              </Typography>
            )}
            {!isLoading && filteredServices.length > 0 && (
              <List
                ref={listRef}
                style={{ overflow: 'auto' }}
                height={filteredServices.length > 0 ? Math.max(filteredServices.length * 100, 300) : 300}
                rowCount={filteredServices.length}
                rowHeight={100}
                width={497}
                rowRenderer={rowRenderer}
              />
            )}
          </Box>
        </Box>
      )}
    </>
  );
});

export { FavoriteServices };
